import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { Class, Student, ClassFormData, StudentFormData } from '../types';

const CLASSES_COLLECTION = 'classes';
const STUDENTS_COLLECTION = 'students';

// Helper function to safely convert Firestore timestamps to dates
const safeToDate = (timestamp: any): Date | undefined => {
  if (!timestamp) return undefined;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return undefined;
};

export class ClassService {
  // Get all classes with pagination
  static async getClasses(pageSize: number = 20, lastDoc?: QueryDocumentSnapshot<DocumentData>) {
    try {
      let q = query(
        collection(getDB(), CLASSES_COLLECTION),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const classes: Class[] = [];
      
      for (const classDoc of snapshot.docs) {
        const data = classDoc.data();
        const students = await this.getStudentsByClassId(classDoc.id);
        
        classes.push({
          id: classDoc.id,
          ...data,
          students,
          createdAt: safeToDate(data.createdAt) || new Date()
        } as Class);
      }

      return {
        classes,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        hasMore: snapshot.docs.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching classes:', error);
      throw new Error('Failed to fetch classes');
    }
  }

  // Get class by ID
  static async getClassById(classId: string): Promise<Class | null> {
    try {
      const docRef = doc(getDB(), CLASSES_COLLECTION, classId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      const students = await this.getStudentsByClassId(classId);
      
      return {
        id: docSnap.id,
        ...data,
        students,
        createdAt: safeToDate(data.createdAt) || new Date()
      } as Class;
    } catch (error) {
      console.error('Error fetching class:', error);
      throw new Error('Failed to fetch class');
    }
  }

  // Create new class
  static async createClass(classData: ClassFormData): Promise<Class> {
    try {
      // Create class document
      const classDoc = {
        name: classData.name,
        strength: classData.strength,
        createdAt: new Date()
      };

      const classDocRef = await addDoc(collection(getDB(), CLASSES_COLLECTION), classDoc);
      
      // Create students for this class
      const students: Student[] = [];
      for (const studentData of classData.students) {
        const student = await this.createStudent({
          ...studentData,
          classId: classDocRef.id
        });
        students.push(student);
      }

      return {
        id: classDocRef.id,
        ...classDoc,
        students
      } as Class;
    } catch (error) {
      console.error('Error creating class:', error);
      throw new Error('Failed to create class');
    }
  }

  // Update class
  static async updateClass(classId: string, updates: Partial<Class>): Promise<void> {
    try {
      const docRef = doc(getDB(), CLASSES_COLLECTION, classId);
      const updateData = {
        ...updates,
        updatedAt: new Date()
      };
      
      // Remove undefined values and students array (handled separately)
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined || key === 'students') {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating class:', error);
      throw new Error('Failed to update class');
    }
  }

  // Delete class
  static async deleteClass(classId: string): Promise<void> {
    try {
      // First delete all students in this class
      const students = await this.getStudentsByClassId(classId);
      for (const student of students) {
        await this.deleteStudent(student.id);
      }

      // Then delete the class
      const docRef = doc(getDB(), CLASSES_COLLECTION, classId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting class:', error);
      throw new Error('Failed to delete class');
    }
  }

  // Get total class count
  static async getTotalClassCount(): Promise<number> {
    try {
      const snapshot = await getDocs(collection(getDB(), CLASSES_COLLECTION));
      return snapshot.size;
    } catch (error) {
      console.error('Error getting class count:', error);
      throw new Error('Failed to get class count');
    }
  }

  // Get all classes without pagination (for internal use)
  static async getAllClasses(): Promise<Class[]> {
    try {
      const snapshot = await getDocs(collection(getDB(), CLASSES_COLLECTION));
      const classes: Class[] = [];
      
      for (const classDoc of snapshot.docs) {
        const data = classDoc.data();
        const students = await this.getStudentsByClassId(classDoc.id);
        
        classes.push({
          id: classDoc.id,
          ...data,
          students,
          createdAt: safeToDate(data.createdAt) || new Date()
        } as Class);
      }

      return classes;
    } catch (error) {
      console.error('Error fetching all classes:', error);
      throw new Error('Failed to fetch all classes');
    }
  }

  // Student operations
  static async getStudentsByClassId(classId: string): Promise<Student[]> {
    try {
      // Get the class document
      const classDocRef = doc(getDB(), CLASSES_COLLECTION, classId);
      const classDoc = await getDoc(classDocRef);
      
      if (!classDoc.exists()) {
        return [];
      }

      const classData = classDoc.data();
      const students: Student[] = [];
      
      // Check if there's an attendance field with student data
      if (classData.attendance && Array.isArray(classData.attendance)) {
        // Extract unique students from attendance records
        const studentMap = new Map<string, Student>();
        
        classData.attendance.forEach((record: any, index: number) => {
          if (record.name && !studentMap.has(record.name)) {
            studentMap.set(record.name, {
              id: record.id || `student-${index}`,
              name: record.name,
              rollNumber: record.id?.toString() || (index + 1).toString(),
              classId: classId,
              email: record.email || '',
              phone: record.phone || ''
            });
          }
        });
        
                 students.push(...Array.from(studentMap.values()));
      }

      // Also check for a students array directly in the class document
      if (classData.students && Array.isArray(classData.students)) {
        classData.students.forEach((studentData: any, index: number) => {
          students.push({
            id: studentData.id || `student-${students.length + index}`,
            name: studentData.name,
            rollNumber: studentData.rollNumber || studentData.id || (students.length + index + 1).toString(),
            classId: classId,
            email: studentData.email || '',
            phone: studentData.phone || ''
          });
        });
      }

      // Sort by name
      students.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return students;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw new Error('Failed to fetch students');
    }
  }

  static async createStudent(studentData: StudentFormData & { classId: string }): Promise<Student> {
    try {
      const classDocRef = doc(getDB(), CLASSES_COLLECTION, studentData.classId);
      const classDoc = await getDoc(classDocRef);
      
      if (!classDoc.exists()) {
        throw new Error('Class not found');
      }

      const classData = classDoc.data();
      const newStudent = {
        id: `student-${Date.now()}`,
        name: studentData.name,
        rollNumber: studentData.rollNumber,
        classId: studentData.classId,
        email: studentData.email || '',
        phone: studentData.phone || '',
        createdAt: new Date()
      };

      // Initialize students array if it doesn't exist
      const students = classData.students || [];
      students.push(newStudent);

      // Update the class document with the new student
      await updateDoc(classDocRef, {
        students: students,
        updatedAt: new Date()
      });

      return newStudent as Student;
    } catch (error) {
      console.error('Error creating student:', error);
      throw new Error('Failed to create student');
    }
  }

  static async updateStudent(studentId: string, updates: Partial<Student>): Promise<void> {
    try {
      // Find which class contains this student
      const classesSnapshot = await getDocs(collection(getDB(), CLASSES_COLLECTION));
      
      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();
        const students = classData.students || [];
        
        const studentIndex = students.findIndex((s: any) => s.id === studentId);
        if (studentIndex !== -1) {
          // Update the student in the array
          students[studentIndex] = {
            ...students[studentIndex],
            ...updates,
            updatedAt: new Date()
          };
          
          // Update the class document
          await updateDoc(doc(getDB(), CLASSES_COLLECTION, classDoc.id), {
            students: students,
            updatedAt: new Date()
          });
          return;
        }
      }
      
      throw new Error('Student not found');
    } catch (error) {
      console.error('Error updating student:', error);
      throw new Error('Failed to update student');
    }
  }

  static async deleteStudent(studentId: string): Promise<void> {
    try {
      // Find which class contains this student
      const classesSnapshot = await getDocs(collection(getDB(), CLASSES_COLLECTION));
      
      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();
        const students = classData.students || [];
        
        const studentIndex = students.findIndex((s: any) => s.id === studentId);
        if (studentIndex !== -1) {
          // Remove the student from the array
          students.splice(studentIndex, 1);
          
          // Update the class document
          await updateDoc(doc(getDB(), CLASSES_COLLECTION, classDoc.id), {
            students: students,
            updatedAt: new Date()
          });
          return;
        }
      }
      
      throw new Error('Student not found');
    } catch (error) {
      console.error('Error deleting student:', error);
      throw new Error('Failed to delete student');
    }
  }

  // Get total student count
  static async getTotalStudentCount(): Promise<number> {
    try {
      const snapshot = await getDocs(collection(getDB(), CLASSES_COLLECTION));
      let totalStudents = 0;
      
      snapshot.forEach((doc) => {
        const classData = doc.data();
        const students = classData.students || [];
        totalStudents += students.length;
      });
      
      return totalStudents;
    } catch (error) {
      console.error('Error getting student count:', error);
      throw new Error('Failed to get student count');
    }
  }

  // Search classes (simplified to avoid composite index requirements)
  static async searchClasses(searchTerm: string): Promise<Class[]> {
    try {
      const q = query(
        collection(getDB(), CLASSES_COLLECTION)
      );

      const snapshot = await getDocs(q);
      const classes: Class[] = [];
      
      for (const classDoc of snapshot.docs) {
        const data = classDoc.data();
        
        // Client-side search filtering
        const searchLower = searchTerm.toLowerCase();
        if (data.name && data.name.toLowerCase().includes(searchLower)) {
          const students = await this.getStudentsByClassId(classDoc.id);
          const classObj: Class = {
            id: classDoc.id,
            name: data.name,
            strength: data.strength,
            students,
            createdAt: safeToDate(data.createdAt) || new Date(),
            subjects: data.subjects || []
          };
          classes.push(classObj);
        }
      }

      return classes;
    } catch (error) {
      console.error('Error searching classes:', error);
      throw new Error('Failed to search classes');
    }
  }

  // Get classes by faculty (simplified to avoid composite index requirements)
  static async getClassesByFaculty(facultyId: string): Promise<Class[]> {
    try {
      const q = query(
        collection(getDB(), CLASSES_COLLECTION),
        where('facultyId', '==', facultyId)
      );

      const snapshot = await getDocs(q);
      const classes: Class[] = [];
      
      for (const classDoc of snapshot.docs) {
        const data = classDoc.data();
        const students = await this.getStudentsByClassId(classDoc.id);
        
        classes.push({
          id: classDoc.id,
          ...data,
          students,
          createdAt: safeToDate(data.createdAt) || new Date()
        } as Class);
      }

      return classes;
    } catch (error) {
      console.error('Error fetching classes by faculty:', error);
      throw new Error('Failed to fetch classes by faculty');
    }
  }

  // Save attendance for a class
  static async saveAttendance(classId: string, date: string, attendanceRecords: { studentId: string; present: boolean }[]): Promise<void> {
    try {
      const classDocRef = doc(getDB(), CLASSES_COLLECTION, classId);
      const classDoc = await getDoc(classDocRef);
      
      if (!classDoc.exists()) {
        throw new Error('Class not found');
      }

      const classData = classDoc.data();
      const students = classData.students || [];

      // Update each student's attendance array
      const updatedStudents = students.map((student: any) => {
        const attendanceRecord = attendanceRecords.find(record => record.studentId === student.id);
        
        if (attendanceRecord) {
          // Initialize attendance array if it doesn't exist
          const attendance = student.attendance || [];
          
          // Check if attendance for this date already exists
          const existingAttendanceIndex = attendance.findIndex((record: any) => record.date === date);
          
          const newAttendanceRecord = {
            date: date,
            status: attendanceRecord.present ? 'Present' : 'Absent'
          };

          if (existingAttendanceIndex !== -1) {
            // Update existing attendance record
            attendance[existingAttendanceIndex] = newAttendanceRecord;
          } else {
            // Add new attendance record
            attendance.push(newAttendanceRecord);
          }

          return {
            ...student,
            attendance: attendance
          };
        }
        
        return student;
      });

      // Update the class document with the updated students
      await updateDoc(classDocRef, {
        students: updatedStudents,
        updatedAt: new Date()
      });

    } catch (error) {
      console.error('Error saving attendance:', error);
      throw new Error('Failed to save attendance');
    }
  }

} 
